/**
 * CreateDocumentWorkspaceWizard.tsx
 * 
 * A 3-step wizard for creating document workspaces with AI generation:
 * 
 * Step 1: Name, Description & Format
 * Step 2: Starting Document & Reference Document (optional)
 * Step 3: Generation Prompt & Create
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
    Modal,
    Spinner,
    SpinnerSize
} from '@fluentui/react';
import {
    DocumentRegular,
    ArrowRightFilled,
    ArrowLeftFilled,
    SparkleRegular,
    DismissRegular,
    CloudArrowUpRegular,
    DocumentTextRegular,
    DocumentPdfRegular,
    DeleteRegular,
    SlideTextRegular,
    CheckmarkCircleFilled
} from '@fluentui/react-icons';
import { 
    createAndGenerateWorkspace,
    createTenderWorkspace,
    DocumentWorkspace,
    TenderWorkspace
} from '../../api/documentWorkspaceApi';
import { authenticatedFetch } from '../../utils/authApi';
import { buildApiUrl } from '../../config/apiConfig';

// ============================================================================
// TYPES
// ============================================================================

interface CreateDocumentWorkspaceWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (workspace: DocumentWorkspace) => void;
}

type WizardStep = 1 | 2 | 3 | 4;  // 4 steps for tender mode

interface WebSource {
    url: string;
    description: string;
}

interface UploadedDocument {
    id: string;
    name: string;
    size: number;
    description?: string;  // User-provided description of what the file contains
}

// ============================================================================
// CONSTANTS
// ============================================================================

const SUPPORTED_EXTENSIONS = ['.pdf', '.docx', '.doc', '.txt'];

const EXAMPLE_PROMPTS = [
    'Based on the reference document, create a design document for...',
    'Create a comprehensive proposal for...',
    'Write a technical specification based on...',
    'Summarize and expand on the starting document for...'
];

// More specific prompts for quick selection
const QUICK_START_PROMPTS = [
    {
        label: '📋 Project Proposal',
        prompt: 'Create a professional project proposal that includes: executive summary, project objectives and scope, timeline and milestones, resource requirements, budget breakdown, risk assessment, and success metrics. Structure it for stakeholder presentation.'
    },
    {
        label: '📄 Technical Documentation',
        prompt: 'Generate comprehensive technical documentation covering: system architecture overview, component descriptions, API specifications, data flow diagrams, installation/deployment steps, configuration options, troubleshooting guide, and maintenance procedures.'
    },
    {
        label: '📊 Business Case',
        prompt: 'Develop a detailed business case document with: problem statement, proposed solution, cost-benefit analysis, ROI projections, implementation approach, risk mitigation strategies, stakeholder impact assessment, and recommendation summary.'
    },
    {
        label: '🎯 RFP Response',
        prompt: 'Create an RFP response based on the provided requirements document. Include: executive summary addressing key evaluation criteria, company qualifications, proposed approach and methodology, project timeline, team structure, pricing breakdown, and relevant case studies.'
    },
    {
        label: '📝 Process Document',
        prompt: 'Write a detailed process documentation that covers: process overview and objectives, step-by-step procedures, roles and responsibilities, decision points and escalation paths, quality checkpoints, required tools and systems, and process metrics.'
    },
    {
        label: '🔍 Analysis Report',
        prompt: 'Generate an analysis report based on the source documents. Include: executive summary, methodology description, key findings and insights, data analysis with visualizations, recommendations, implementation considerations, and appendices with supporting data.'
    }
];

// ============================================================================
// COMPONENT
// ============================================================================

export const CreateDocumentWorkspaceWizard: React.FC<CreateDocumentWorkspaceWizardProps> = ({
    isOpen,
    onClose,
    onCreated
}) => {
    // Step state
    const [step, setStep] = useState<WizardStep>(1);
    
    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [format, setFormat] = useState<'docx' | 'pdf' | 'pptx'>('docx');
    const [generationPrompt, setGenerationPrompt] = useState('');
    
    // Workspace mode: 'standard' (current flow) or 'tender' (tender response flow)
    const [workspaceMode, setWorkspaceMode] = useState<'standard' | 'tender'>('standard');
    
    // Document state
    const [startingDoc, setStartingDoc] = useState<UploadedDocument | null>(null);
    const [referenceDocs, setReferenceDocs] = useState<UploadedDocument[]>([]);  // Multiple reference docs
    const [uploadingType, setUploadingType] = useState<'starting' | 'reference' | null>(null);
    
    // Tender mode: document roles for each reference doc
    const [docRoles, setDocRoles] = useState<Record<string, 'evidence' | 'requirements' | 'standards' | 'commercial' | 'custom'>>({});
    // Custom role text when 'custom' is selected
    const [customRoleText, setCustomRoleText] = useState<Record<string, string>>({});
    
    // Data source options (simple toggles for UC1/UC2/UC3)
    const [useReferencesOnly, setUseReferencesOnly] = useState(false);  // UC2: Ignore AI knowledge
    const [enableWebSearch, setEnableWebSearch] = useState(false);      // UC1/UC2: Live search
    const [allowedDomains, setAllowedDomains] = useState('');           // Optional: restrict search sites
    
    // AI Instructions (custom guidance for AI behavior)
    const [aiInstructions, setAiInstructions] = useState('');
    
    // Generation mode: 'ai' (single-shot) or 'document_intelligence' (section-by-section with DI)
    const [generationMode, setGenerationMode] = useState<'ai' | 'document_intelligence'>('ai');
    
    // Web sources (specific URLs to fetch from for tender mode)
    const [webSources, setWebSources] = useState<WebSource[]>([]);
    const [newWebSourceUrl, setNewWebSourceUrl] = useState('');
    const [newWebSourceDescription, setNewWebSourceDescription] = useState('');
    
    // General state
    const [error, setError] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isDragging, setIsDragging] = useState<'starting' | 'reference' | null>(null);
    
    // Generation progress state (for animated thinking messages)
    const [generationPhase, setGenerationPhase] = useState<string>('');
    const [generationMessages, setGenerationMessages] = useState<string[]>([]);
    
    // Presentation template state
    interface PresentationTemplate {
        id: string;
        name: string;
        description: string;
        thumbnail_url: string | null;
        is_default: boolean;
    }
    const [templates, setTemplates] = useState<PresentationTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    
    const startingInputRef = useRef<HTMLInputElement>(null);
    const referenceInputRef = useRef<HTMLInputElement>(null);

    // Fetch presentation templates when pptx is selected
    useEffect(() => {
        if (format === 'pptx' && templates.length === 0 && !loadingTemplates) {
            setLoadingTemplates(true);
            authenticatedFetch('/api/presentation-templates')
                .then(r => r.json())
                .then(data => {
                    if (data.success && data.templates) {
                        setTemplates(data.templates);
                        // Auto-select the default template
                        const defaultTemplate = data.templates.find((t: PresentationTemplate) => t.is_default);
                        if (defaultTemplate) {
                            setSelectedTemplateId(defaultTemplate.id);
                        } else if (data.templates.length > 0) {
                            setSelectedTemplateId(data.templates[0].id);
                        }
                    }
                })
                .catch(() => {
                    // Templates are optional, ignore errors
                })
                .finally(() => setLoadingTemplates(false));
        }
    }, [format, templates.length, loadingTemplates]);

    const handleClose = useCallback(() => {
        setStep(1);
        setTitle('');
        setDescription('');
        setFormat('docx');
        setGenerationPrompt('');
        setStartingDoc(null);
        setReferenceDocs([]);
        setSelectedTemplateId(null);
        setError(null);
        setIsCreating(false);
        onClose();
    }, [onClose]);

    const handleUploadDocument = async (file: File, type: 'starting' | 'reference') => {
        // Validate extension
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!SUPPORTED_EXTENSIONS.includes(ext)) {
            setError(`Unsupported file type. Supported: ${SUPPORTED_EXTENSIONS.join(', ')}`);
            return;
        }
        
        setUploadingType(type);
        setError(null);
        
        try {
            // Upload using SAS (direct-to-blob) for better scalability
            const { sasUpload } = await import('../../utils/sasUpload');
            const result = await sasUpload(file, {
                ownerType: 'workspace'
            });
            
            if (!result.success) {
                throw new Error(result.error || 'Upload failed');
            }
            
            const uploadedDoc: UploadedDocument = {
                id: result.fileId!,
                name: file.name,
                size: file.size
            };
            
            if (type === 'starting') {
                setStartingDoc(uploadedDoc);
            } else {
                // Add to reference docs array
                setReferenceDocs(prev => [...prev, uploadedDoc]);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to upload document');
        } finally {
            setUploadingType(null);
        }
    };

    const handleFileDrop = (e: React.DragEvent, type: 'starting' | 'reference') => {
        e.preventDefault();
        setIsDragging(null);
        
        const files = Array.from(e.dataTransfer.files);
        if (type === 'starting' && files.length > 0) {
            // Single file for starting
            handleUploadDocument(files[0], type);
        } else if (type === 'reference') {
            // Multiple files for reference
            files.forEach(file => handleUploadDocument(file, type));
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'starting' | 'reference') => {
        if (e.target.files && e.target.files.length > 0) {
            if (type === 'starting') {
                // Single file for starting
                handleUploadDocument(e.target.files[0], type);
            } else {
                // Multiple files for reference
                Array.from(e.target.files).forEach(file => handleUploadDocument(file, type));
            }
        }
        e.target.value = '';
    };

    const handleCreate = async () => {
        if (!title.trim()) {
            setError('Please enter a document title');
            return;
        }
        
        setIsCreating(true);
        setError(null);
        
        // For tender mode - use different API that creates but doesn't generate
        if (workspaceMode === 'tender') {
            if (!startingDoc) {
                setError('Please upload a tender template document');
                setIsCreating(false);
                return;
            }
            
            setGenerationPhase('Creating tender workspace...');
            setGenerationMessages(['Setting up tender analysis pipeline...']);
            
            try {
                // Build roles map - use custom text for 'custom' roles
                const roles: Record<string, string> = {};
                referenceDocs.forEach(d => {
                    const role = docRoles[d.id] || 'requirements';
                    if (role === 'custom') {
                        roles[d.id] = customRoleText[d.id] || 'other';
                    } else {
                        roles[d.id] = role;
                    }
                });
                
                const workspace = await createTenderWorkspace({
                    title: title.trim(),
                    description: description.trim(),
                    templateDocumentId: startingDoc.id,
                    templateDocumentDescription: startingDoc.description || '',
                    referenceDocumentIds: referenceDocs.map(d => d.id),
                    referenceDocumentRoles: roles,
                    // AI instructions
                    aiInstructions: aiInstructions.trim(),
                    // Data source options
                    useReferencesOnly: true,  // Always use refs only for tender
                    enableWebSearch,
                    allowedDomains: allowedDomains.trim() || undefined,
                    webSources: webSources.filter(s => s.url.trim())
                });
                
                // Return the tender workspace - caller should route to TenderBuilder
                onCreated(workspace as unknown as DocumentWorkspace);
                handleClose();
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to create tender workspace');
                setIsCreating(false);
                setGenerationPhase('');
                setGenerationMessages([]);
            }
            return;
        }
        
        // Standard mode - generate content immediately
        const finalPrompt = generationPrompt.trim();
        if (!finalPrompt) {
            setError('Please enter a generation prompt');
            setIsCreating(false);
            return;
        }
        
        setGenerationPhase('Initializing...');
        setGenerationMessages([]);
        
        // Animated progress messages (since we don't have streaming yet)
        const progressMessages = [
            { delay: 0, phase: 'Initializing...', message: 'Preparing workspace...' },
            { delay: 2000, phase: 'Analyzing Documents', message: 'Reading uploaded documents...' },
            { delay: 5000, phase: 'Analyzing Documents', message: 'Extracting key information from references...' },
            { delay: 10000, phase: 'Creating Outline', message: 'Building document structure...' },
            { delay: 15000, phase: 'Creating Outline', message: 'Mapping content to sections...' },
            { delay: 20000, phase: 'Generating Content', message: 'Writing introduction...' },
            { delay: 30000, phase: 'Generating Content', message: 'Generating section content...' },
            { delay: 45000, phase: 'Generating Content', message: 'Expanding details...' },
            { delay: 60000, phase: 'Reviewing', message: 'Reviewing document quality...' },
            { delay: 75000, phase: 'Finalizing', message: 'Preparing final document...' }
        ];
        
        // Set up progress timers
        const timers: NodeJS.Timeout[] = [];
        progressMessages.forEach(({ delay, phase, message }) => {
            const timer = setTimeout(() => {
                setGenerationPhase(phase);
                setGenerationMessages(prev => [...prev.slice(-4), message]); // Keep last 5 messages
            }, delay);
            timers.push(timer);
        });
        
        try {
            const workspace = await createAndGenerateWorkspace({
                title: title.trim(),
                description: description.trim(),
                format,
                generationPrompt: finalPrompt,
                startingDocumentId: startingDoc?.id,
                startingDocumentDescription: startingDoc?.description,
                referenceDocumentIds: referenceDocs.map(d => d.id),
                referenceDocumentDescriptions: referenceDocs.map(d => d.description || ''),
                // Data source options
                useReferencesOnly,
                enableWebSearch,
                allowedDomains: allowedDomains.trim() || undefined,
                // Generation mode
                generationMode,
                // AI instructions
                aiInstructions: aiInstructions.trim(),
                // Web sources
                webSources: webSources.filter(s => s.url.trim()),
                // Presentation template (for pptx)
                templateId: format === 'pptx' ? selectedTemplateId || undefined : undefined
            });
            
            // Clear timers
            timers.forEach(t => clearTimeout(t));
            
            onCreated(workspace);
            handleClose();
        } catch (err) {
            // Clear timers
            timers.forEach(t => clearTimeout(t));
            setError(err instanceof Error ? err.message : 'Failed to create workspace');
            setIsCreating(false);
            setGenerationPhase('');
            setGenerationMessages([]);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Dynamic step labels based on mode
    // Tender mode has 4 steps: Details → Documents → Instructions & Sources → Review
    // Standard mode has 3 steps: Details → Documents → Generate
    const stepTitles = workspaceMode === 'tender' 
        ? ['Tender Details', 'Tender Documents', 'Instructions & Sources', 'Review & Start']
        : ['Document Details', 'Source Documents', 'Generate Content'];
    const stepDescriptions = workspaceMode === 'tender'
        ? [
            'Name your tender response and choose mode',
            'Upload template and requirements documents with roles',
            'Add AI instructions and configure data sources',
            'Review configuration and start tender analysis'
        ]
        : [
            'Name your document and choose the output format',
            'Add documents to base your content on (optional)',
            'Describe what you want to create'
        ];
    
    const maxSteps = workspaceMode === 'tender' ? 4 : 3;

    const canProceed = () => {
        if (step === 1) return title.trim().length > 0;
        if (step === 2) {
            if (workspaceMode === 'tender') {
                // Tender mode requires template document AND at least one reference doc
                return !!startingDoc && referenceDocs.length > 0;
            }
            return true; // Documents are optional in standard mode
        }
        if (step === 3) {
            if (workspaceMode === 'tender') {
                // Instructions & Sources step - optional, always can proceed
                return true;
            }
            // Standard mode: need generation prompt
            return generationPrompt.trim().length > 0;
        }
        if (step === 4) {
            // Review step for tender mode - always can proceed if we got here
            return true;
        }
        return false;
    };
    
    // Helper to add web source
    const addWebSource = () => {
        if (!newWebSourceUrl.trim()) return;
        setWebSources(prev => [...prev, { 
            url: newWebSourceUrl.trim(), 
            description: newWebSourceDescription.trim() 
        }]);
        setNewWebSourceUrl('');
        setNewWebSourceDescription('');
    };
    
    const removeWebSource = (index: number) => {
        setWebSources(prev => prev.filter((_, i) => i !== index));
    };

    // ========================================================================
    // RENDER HELPERS
    // ========================================================================

    // Single document upload (for starting document)
    const renderSingleDocumentUploadZone = (
        doc: UploadedDocument | null,
        label: string,
        description: string
    ) => {
        const isUploading = uploadingType === 'starting';
        const isDragOver = isDragging === 'starting';
        
        return (
            <div style={{ marginBottom: '20px' }}>
                <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '8px'
                }}>
                    {label}
                </label>
                <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280' }}>
                    {description}
                </p>
                
                {doc ? (
                    // Show uploaded document with description field
                    <div style={{
                        padding: '14px 16px',
                        background: '#f0fdf4',
                        border: '1px solid #86efac',
                        borderRadius: '10px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '12px'
                        }}>
                            <DocumentTextRegular style={{ fontSize: '24px', color: '#16a34a' }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 500, color: '#166534', fontSize: '14px' }}>
                                    {doc.name}
                                </div>
                                <div style={{ fontSize: '12px', color: '#4ade80' }}>
                                    {formatFileSize(doc.size)}
                                </div>
                            </div>
                            <button
                                onClick={() => setStartingDoc(null)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '6px',
                                    color: '#dc2626',
                                    borderRadius: '4px'
                                }}
                            >
                                <DeleteRegular style={{ fontSize: '18px' }} />
                            </button>
                        </div>
                        {/* Description field */}
                        <input
                            type="text"
                            placeholder="Describe what this template contains (optional)"
                            value={doc.description || ''}
                            onChange={(e) => setStartingDoc({ ...doc, description: e.target.value })}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #86efac',
                                borderRadius: '6px',
                                fontSize: '13px',
                                background: 'white'
                            }}
                        />
                    </div>
                ) : (
                    // Upload zone
                    <div
                        onDragOver={(e) => { e.preventDefault(); setIsDragging('starting'); }}
                        onDragLeave={() => setIsDragging(null)}
                        onDrop={(e) => handleFileDrop(e, 'starting')}
                        onClick={() => startingInputRef.current?.click()}
                        style={{
                            padding: '28px',
                            border: `2px dashed ${isDragOver ? '#667eea' : '#d1d5db'}`,
                            borderRadius: '12px',
                            background: isDragOver ? 'rgba(102,126,234,0.05)' : '#f9fafb',
                            cursor: isUploading ? 'wait' : 'pointer',
                            textAlign: 'center',
                            transition: 'all 0.2s'
                        }}
                    >
                        <input
                            ref={startingInputRef}
                            type="file"
                            accept={SUPPORTED_EXTENSIONS.join(',')}
                            onChange={(e) => handleFileSelect(e, 'starting')}
                            style={{ display: 'none' }}
                        />
                        
                        {isUploading ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                <Spinner size={SpinnerSize.small} />
                                <span style={{ color: '#6b7280', fontSize: '14px' }}>Uploading...</span>
                            </div>
                        ) : (
                            <>
                                <CloudArrowUpRegular style={{ fontSize: '32px', color: '#9ca3af', marginBottom: '8px' }} />
                                <div style={{ color: '#374151', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                                    Drop file here or click to browse
                                </div>
                                <div style={{ color: '#9ca3af', fontSize: '12px' }}>
                                    Supports PDF, DOCX, DOC, TXT
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Multiple document upload (for reference documents)
    const renderMultiDocumentUploadZone = (
        docs: UploadedDocument[],
        label: string,
        description: string
    ) => {
        const isUploading = uploadingType === 'reference';
        const isDragOver = isDragging === 'reference';
        
        const removeDoc = (docId: string) => {
            setReferenceDocs(prev => prev.filter(d => d.id !== docId));
        };
        
        return (
            <div style={{ marginBottom: '20px' }}>
                <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                    marginBottom: '8px'
                }}>
                    {label} {docs.length > 0 && <span style={{ color: '#6b7280', fontWeight: 400 }}>({docs.length} file{docs.length !== 1 ? 's' : ''})</span>}
                </label>
                <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280' }}>
                    {description}
                </p>
                
                {/* Show uploaded documents with description fields */}
                {docs.length > 0 && (
                    <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {docs.map((doc) => (
                            <div key={doc.id} style={{
                                padding: '12px 14px',
                                background: '#f0fdf4',
                                border: '1px solid #86efac',
                                borderRadius: '8px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginBottom: '10px'
                                }}>
                                    <DocumentTextRegular style={{ fontSize: '20px', color: '#16a34a' }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 500, color: '#166534', fontSize: '13px' }}>
                                            {doc.name}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#4ade80' }}>
                                            {formatFileSize(doc.size)}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => removeDoc(doc.id)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            color: '#dc2626',
                                            borderRadius: '4px'
                                        }}
                                    >
                                        <DeleteRegular style={{ fontSize: '16px' }} />
                                    </button>
                                </div>
                                {/* Description field for reference document */}
                                <input
                                    type="text"
                                    placeholder="Describe what this reference contains (optional)"
                                    value={doc.description || ''}
                                    onChange={(e) => {
                                        setReferenceDocs(prev => prev.map(d => 
                                            d.id === doc.id ? { ...d, description: e.target.value } : d
                                        ));
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '8px 10px',
                                        border: '1px solid #86efac',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        background: 'white'
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Upload zone - always show to allow adding more */}
                <div
                    onDragOver={(e) => { e.preventDefault(); setIsDragging('reference'); }}
                    onDragLeave={() => setIsDragging(null)}
                    onDrop={(e) => handleFileDrop(e, 'reference')}
                    onClick={() => referenceInputRef.current?.click()}
                    style={{
                        padding: docs.length > 0 ? '16px' : '28px',
                        border: `2px dashed ${isDragOver ? '#667eea' : '#d1d5db'}`,
                        borderRadius: '12px',
                        background: isDragOver ? 'rgba(102,126,234,0.05)' : '#f9fafb',
                        cursor: isUploading ? 'wait' : 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s'
                    }}
                >
                    <input
                        ref={referenceInputRef}
                        type="file"
                        accept={SUPPORTED_EXTENSIONS.join(',')}
                        multiple  // Allow multiple file selection
                        onChange={(e) => handleFileSelect(e, 'reference')}
                        style={{ display: 'none' }}
                    />
                    
                    {isUploading ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                            <Spinner size={SpinnerSize.small} />
                            <span style={{ color: '#6b7280', fontSize: '14px' }}>Uploading...</span>
                        </div>
                    ) : (
                        <>
                            <CloudArrowUpRegular style={{ fontSize: docs.length > 0 ? '24px' : '32px', color: '#9ca3af', marginBottom: '8px' }} />
                            <div style={{ color: '#374151', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                                {docs.length > 0 ? 'Add more files' : 'Drop files here or click to browse'}
                            </div>
                            <div style={{ color: '#9ca3af', fontSize: '12px' }}>
                                Supports PDF, DOCX, DOC, TXT (multiple files allowed)
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // ========================================================================
    // RENDER
    // ========================================================================

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={handleClose}
            isBlocking={true}
            styles={{
                main: {
                    maxWidth: '640px',
                    width: '90vw',
                    maxHeight: '85vh',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 24px 80px rgba(0,0,0,0.2)'
                },
                scrollableContent: {
                    overflow: 'hidden'
                }
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '80vh', maxHeight: '640px', position: 'relative' }}>
                {/* Header */}
                <div style={{
                    padding: '24px 28px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    position: 'relative'
                }}>
                    <button
                        onClick={handleClose}
                        style={{
                            position: 'absolute',
                            top: '16px',
                            right: '16px',
                            background: 'rgba(255,255,255,0.2)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <DismissRegular style={{ color: 'white', fontSize: '16px' }} />
                    </button>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px' }}>
                        <div style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '12px',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <DocumentRegular style={{ fontSize: '24px' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>
                                {stepTitles[step - 1]}
                            </h1>
                            <p style={{ margin: '2px 0 0', opacity: 0.9, fontSize: '13px' }}>
                                {stepDescriptions[step - 1]}
                            </p>
                        </div>
                    </div>

                    {/* Step indicators */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                        {Array.from({ length: maxSteps }, (_, i) => i + 1).map((s) => (
                            <div
                                key={s}
                                style={{
                                    flex: 1,
                                    height: '4px',
                                    borderRadius: '2px',
                                    background: s <= step ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                                    transition: 'background 0.3s'
                                }}
                            />
                        ))}
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div style={{
                        margin: '16px 28px 0',
                        padding: '12px 16px',
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        color: '#dc2626',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <span>{error}</span>
                        <button
                            onClick={() => setError(null)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: '4px' }}
                        >
                            <DismissRegular />
                        </button>
                    </div>
                )}

                {/* Generation Progress Overlay */}
                {isCreating && (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'rgba(255,255,255,0.97)',
                        zIndex: 100,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '40px',
                        borderRadius: '16px'
                    }}>
                        {/* Animated Icon */}
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '20px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '24px',
                            animation: 'pulse 2s infinite'
                        }}>
                            <SparkleRegular style={{ fontSize: '36px', color: 'white' }} />
                        </div>
                        
                        {/* Phase Title */}
                        <div style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#374151',
                            marginBottom: '8px'
                        }}>
                            {generationPhase || 'Generating Document...'}
                        </div>
                        
                        {/* Spinner */}
                        <Spinner size={SpinnerSize.medium} style={{ marginBottom: '24px' }} />
                        
                        {/* Progress Messages */}
                        <div style={{
                            width: '100%',
                            maxWidth: '400px',
                            minHeight: '120px',
                            padding: '16px',
                            background: '#f9fafb',
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px'
                            }}>
                                {generationMessages.length === 0 ? (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        color: '#6b7280',
                                        fontSize: '13px'
                                    }}>
                                        <span style={{ 
                                            width: '6px', 
                                            height: '6px', 
                                            borderRadius: '50%', 
                                            background: '#667eea',
                                            animation: 'pulse 1s infinite'
                                        }} />
                                        Preparing workspace...
                                    </div>
                                ) : (
                                    generationMessages.map((msg, i) => (
                                        <div 
                                            key={i}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                color: i === generationMessages.length - 1 ? '#374151' : '#9ca3af',
                                                fontSize: '13px',
                                                transition: 'color 0.3s'
                                            }}
                                        >
                                            <span style={{ 
                                                width: '6px', 
                                                height: '6px', 
                                                borderRadius: '50%', 
                                                background: i === generationMessages.length - 1 ? '#667eea' : '#d1d5db'
                                            }} />
                                            {msg}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                        
                        {/* Tip */}
                        <div style={{
                            marginTop: '24px',
                            fontSize: '12px',
                            color: '#9ca3af',
                            textAlign: 'center'
                        }}>
                            This may take 30-90 seconds depending on document complexity
                        </div>
                    </div>
                )}

                {/* Content */}
                <div style={{ flex: 1, overflow: 'auto', padding: '28px' }}>
                    {/* Step 1: Document Details */}
                    {step === 1 && (
                        <div style={{ maxWidth: '480px', margin: '0 auto' }}>
                            {/* Workspace Mode Toggle */}
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#374151',
                                    marginBottom: '12px'
                                }}>
                                    Workspace Type
                                </label>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setWorkspaceMode('standard')}
                                        style={{
                                            flex: 1,
                                            padding: '14px 16px',
                                            border: `2px solid ${workspaceMode === 'standard' ? '#667eea' : '#e5e7eb'}`,
                                            borderRadius: '10px',
                                            background: workspaceMode === 'standard' ? 'rgba(102, 126, 234, 0.08)' : 'white',
                                            cursor: 'pointer',
                                            textAlign: 'left'
                                        }}
                                    >
                                        <div style={{ fontWeight: 600, color: workspaceMode === 'standard' ? '#667eea' : '#374151', marginBottom: '4px' }}>
                                            📄 Standard Document
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                            Create a document with AI assistance
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setWorkspaceMode('tender');
                                            setFormat('docx');  // Tender mode only supports docx
                                            setUseReferencesOnly(true);  // Always use references only in tender mode
                                        }}
                                        style={{
                                            flex: 1,
                                            padding: '14px 16px',
                                            border: `2px solid ${workspaceMode === 'tender' ? '#667eea' : '#e5e7eb'}`,
                                            borderRadius: '10px',
                                            background: workspaceMode === 'tender' ? 'rgba(102, 126, 234, 0.08)' : 'white',
                                            cursor: 'pointer',
                                            textAlign: 'left'
                                        }}
                                    >
                                        <div style={{ fontWeight: 600, color: workspaceMode === 'tender' ? '#667eea' : '#374151', marginBottom: '4px' }}>
                                            📋 Tender Response
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                            Structured tender/RFP response workflow
                                        </div>
                                    </button>
                                </div>
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#374151',
                                    marginBottom: '8px'
                                }}>
                                    {workspaceMode === 'tender' ? 'Tender Response Title' : 'Document Title'} <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder={workspaceMode === 'tender' ? 'e.g., Metro Line Extension - Technical Proposal' : 'e.g., AWS Landing Zone Design'}
                                    autoFocus
                                    style={{
                                        width: '100%',
                                        padding: '14px 16px',
                                        fontSize: '15px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '10px',
                                        outline: 'none',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                />
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#374151',
                                    marginBottom: '8px'
                                }}>
                                    Description
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Brief description of the document purpose..."
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '14px 16px',
                                        fontSize: '14px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '10px',
                                        outline: 'none',
                                        resize: 'vertical',
                                        fontFamily: 'inherit',
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                />
                            </div>

                            {/* Output Format - only show in standard mode */}
                            {workspaceMode === 'standard' && (
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#374151',
                                    marginBottom: '12px'
                                }}>
                                    Output Format
                                </label>
                                <div style={{ display: 'flex', gap: '12px' }}>
                                    {(['docx', 'pdf', 'pptx'] as const).map((fmt) => (
                                        <button
                                            key={fmt}
                                            onClick={() => setFormat(fmt)}
                                            style={{
                                                flex: 1,
                                                padding: '16px',
                                                border: `2px solid ${format === fmt ? '#667eea' : '#e5e7eb'}`,
                                                borderRadius: '10px',
                                                background: format === fmt ? 'rgba(102,126,234,0.05)' : 'white',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            {fmt === 'docx' ? (
                                                <DocumentTextRegular style={{ fontSize: '28px', color: format === fmt ? '#667eea' : '#9ca3af' }} />
                                            ) : fmt === 'pdf' ? (
                                                <DocumentPdfRegular style={{ fontSize: '28px', color: format === fmt ? '#667eea' : '#9ca3af' }} />
                                            ) : (
                                                <SlideTextRegular style={{ fontSize: '28px', color: format === fmt ? '#667eea' : '#9ca3af' }} />
                                            )}
                                            <span style={{ 
                                                fontWeight: 600, 
                                                color: format === fmt ? '#667eea' : '#6b7280',
                                                textTransform: 'uppercase',
                                                fontSize: '13px'
                                            }}>
                                                {fmt}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            )}

                            {/* Template selection for presentations */}
                            {format === 'pptx' && (
                                <div style={{ marginTop: '24px' }}>
                                    <label style={{
                                        display: 'block',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: '#374151',
                                        marginBottom: '12px'
                                    }}>
                                        Presentation Template
                                    </label>
                                    {loadingTemplates ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280' }}>
                                            <Spinner size={SpinnerSize.small} />
                                            Loading templates...
                                        </div>
                                    ) : templates.length === 0 ? (
                                        <div style={{
                                            padding: '16px',
                                            background: '#f9fafb',
                                            border: '1px dashed #d1d5db',
                                            borderRadius: '8px',
                                            color: '#6b7280',
                                            fontSize: '14px'
                                        }}>
                                            No templates available. Using default PowerPoint styling.
                                        </div>
                                    ) : (
                                        <div style={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', 
                                            gap: '12px' 
                                        }}>
                                            {templates.map(template => (
                                                <button
                                                    key={template.id}
                                                    onClick={() => setSelectedTemplateId(template.id)}
                                                    style={{
                                                        padding: '12px',
                                                        border: `2px solid ${selectedTemplateId === template.id ? '#667eea' : '#e5e7eb'}`,
                                                        borderRadius: '10px',
                                                        background: selectedTemplateId === template.id ? 'rgba(102,126,234,0.05)' : 'white',
                                                        cursor: 'pointer',
                                                        textAlign: 'center',
                                                        position: 'relative'
                                                    }}
                                                >
                                                    {selectedTemplateId === template.id && (
                                                        <CheckmarkCircleFilled style={{
                                                            position: 'absolute',
                                                            top: '6px',
                                                            right: '6px',
                                                            color: '#667eea',
                                                            fontSize: '18px'
                                                        }} />
                                                    )}
                                                    <div style={{
                                                        height: '60px',
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        borderRadius: '6px',
                                                        marginBottom: '8px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {template.thumbnail_url ? (
                                                            <img 
                                                                src={buildApiUrl(template.thumbnail_url)} 
                                                                alt={template.name}
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '6px' }}
                                                            />
                                                        ) : (
                                                            <SlideTextRegular style={{ fontSize: '28px', color: 'rgba(255,255,255,0.7)' }} />
                                                        )}
                                                    </div>
                                                    <span style={{
                                                        display: 'block',
                                                        fontSize: '13px',
                                                        fontWeight: 600,
                                                        color: selectedTemplateId === template.id ? '#667eea' : '#374151',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {template.name}
                                                    </span>
                                                    {template.is_default && (
                                                        <span style={{
                                                            fontSize: '10px',
                                                            color: '#9ca3af',
                                                            display: 'block',
                                                            marginTop: '2px'
                                                        }}>
                                                            Default
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 2: Source Documents */}
                    {step === 2 && (
                        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
                            {workspaceMode === 'tender' ? (
                                // Tender mode: Template document + Reference documents with roles
                                <>
                                    {renderSingleDocumentUploadZone(
                                        startingDoc,
                                        '📄 Template Document *',
                                        'Upload the tender response template - this defines your document structure'
                                    )}
                                    
                                    {/* Divider */}
                                    <div style={{ margin: '24px 0', borderTop: '1px solid #e5e7eb' }} />
                                    
                                    {/* Multi-document upload with role selector */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '8px'
                                        }}>
                                            📋 Tender Documents {referenceDocs.length > 0 && <span style={{ color: '#6b7280', fontWeight: 400 }}>({referenceDocs.length} file{referenceDocs.length !== 1 ? 's' : ''})</span>}
                                        </label>
                                        <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280' }}>
                                            Upload requirements (RFP/ITT), evidence (case studies, certificates), standards, and commercial documents.
                                        </p>
                                        
                                        {/* Show uploaded documents with role dropdown */}
                                        {referenceDocs.length > 0 && (
                                            <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {referenceDocs.map((doc) => (
                                                    <div key={doc.id} style={{
                                                        padding: '12px 14px',
                                                        background: '#f0fdf4',
                                                        border: '1px solid #86efac',
                                                        borderRadius: '8px'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px',
                                                            marginBottom: '10px'
                                                        }}>
                                                            <DocumentTextRegular style={{ fontSize: '20px', color: '#16a34a' }} />
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontWeight: 500, color: '#166534', fontSize: '13px' }}>
                                                                    {doc.name}
                                                                </div>
                                                                <div style={{ fontSize: '11px', color: '#4ade80' }}>
                                                                    {formatFileSize(doc.size)}
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={() => setReferenceDocs(prev => prev.filter(d => d.id !== doc.id))}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    padding: '4px',
                                                                    color: '#dc2626',
                                                                    borderRadius: '4px'
                                                                }}
                                                            >
                                                                <DeleteRegular style={{ fontSize: '16px' }} />
                                                            </button>
                                                        </div>
                                                        {/* Role dropdown */}
                                                        <select
                                                            value={docRoles[doc.id] || 'requirements'}
                                                            onChange={(e) => setDocRoles(prev => ({ ...prev, [doc.id]: e.target.value as 'requirements' | 'evidence' | 'standards' | 'commercial' | 'custom' }))}
                                                            style={{
                                                                width: '100%',
                                                                padding: '8px 10px',
                                                                border: '1px solid #86efac',
                                                                borderRadius: '6px',
                                                                fontSize: '12px',
                                                                background: 'white',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <option value="requirements">📋 Requirements (RFP/ITT)</option>
                                                            <option value="evidence">📄 Evidence (case studies, certificates)</option>
                                                            <option value="standards">📏 Standards & Policies</option>
                                                            <option value="commercial">💰 Commercial/Pricing</option>
                                                            <option value="custom">✏️ Custom type...</option>
                                                        </select>
                                                        {/* Custom role text input */}
                                                        {docRoles[doc.id] === 'custom' && (
                                                            <input
                                                                type="text"
                                                                value={customRoleText[doc.id] || ''}
                                                                onChange={(e) => setCustomRoleText(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                                                placeholder="Enter document type (e.g., Technical specs, Reference letter)"
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '8px 10px',
                                                                    border: '1px solid #d1d5db',
                                                                    borderRadius: '6px',
                                                                    fontSize: '12px',
                                                                    marginTop: '8px',
                                                                    boxSizing: 'border-box'
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Upload zone for more files */}
                                        <div
                                            onDragOver={(e) => { e.preventDefault(); setIsDragging('reference'); }}
                                            onDragLeave={() => setIsDragging(null)}
                                            onDrop={(e) => handleFileDrop(e, 'reference')}
                                            onClick={() => referenceInputRef.current?.click()}
                                            style={{
                                                padding: referenceDocs.length > 0 ? '16px' : '28px',
                                                border: `2px dashed ${isDragging === 'reference' ? '#667eea' : '#d1d5db'}`,
                                                borderRadius: '12px',
                                                background: isDragging === 'reference' ? 'rgba(102,126,234,0.05)' : '#f9fafb',
                                                cursor: uploadingType === 'reference' ? 'wait' : 'pointer',
                                                textAlign: 'center',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <input
                                                ref={referenceInputRef}
                                                type="file"
                                                accept={SUPPORTED_EXTENSIONS.join(',')}
                                                multiple
                                                onChange={(e) => handleFileSelect(e, 'reference')}
                                                style={{ display: 'none' }}
                                            />
                                            
                                            {uploadingType === 'reference' ? (
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                                                    <Spinner size={SpinnerSize.small} />
                                                    <span style={{ color: '#6b7280', fontSize: '14px' }}>Uploading...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <CloudArrowUpRegular style={{ fontSize: referenceDocs.length > 0 ? '24px' : '32px', color: '#9ca3af', marginBottom: '8px' }} />
                                                    <div style={{ color: '#374151', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
                                                        {referenceDocs.length > 0 ? 'Add more documents' : 'Drop files here or click to browse'}
                                                    </div>
                                                    <div style={{ color: '#9ca3af', fontSize: '12px' }}>
                                                        Supports PDF, DOCX, DOC, TXT (multiple files allowed)
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <p style={{ 
                                        margin: '0', 
                                        padding: '12px 16px',
                                        background: '#eff6ff',
                                        border: '1px solid #93c5fd',
                                        borderRadius: '8px',
                                        fontSize: '13px', 
                                        color: '#1e40af' 
                                    }}>
                                        💡 Assign roles to documents to help the AI understand how to use each one in the response generation.
                                    </p>
                                </>
                            ) : (
                                // Standard mode: Original flow
                                <>
                                    {renderSingleDocumentUploadZone(
                                        startingDoc,
                                        'Starting Document',
                                        'A document to expand on or use as base content (single file)'
                                    )}
                                    
                                    {renderMultiDocumentUploadZone(
                                        referenceDocs,
                                        'Reference Documents',
                                        'Documents to use as structure/format reference (multiple allowed)'
                                    )}
                                    
                                    <p style={{ 
                                        margin: '20px 0 0', 
                                        padding: '12px 16px',
                                        background: '#f0f9ff',
                                        border: '1px solid #bae6fd',
                                        borderRadius: '8px',
                                        fontSize: '13px', 
                                        color: '#0369a1' 
                                    }}>
                                        💡 These documents help AI understand the context and structure. 
                                        You can skip this step to create a document from scratch.
                                    </p>
                                </>
                            )}
                        </div>
                    )}

                    {/* Step 3: Generation prompt (standard) or Instructions & Sources (tender) */}
                    {step === 3 && (
                        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
                            {workspaceMode === 'tender' ? (
                                // Tender mode: AI Instructions and Data Sources
                                <>
                                    {/* AI Instructions */}
                                    <div style={{ marginBottom: '24px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '8px'
                                        }}>
                                            🎯 AI Instructions (Optional)
                                        </label>
                                        <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280' }}>
                                            Add specific guidance for the AI to follow when generating your tender response.
                                        </p>
                                        <textarea
                                            value={aiInstructions}
                                            onChange={(e) => setAiInstructions(e.target.value)}
                                            placeholder="Click an example below or write your own instructions..."
                                            rows={4}
                                            style={{
                                                width: '100%',
                                                padding: '14px',
                                                fontSize: '14px',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '10px',
                                                outline: 'none',
                                                resize: 'vertical',
                                                fontFamily: 'inherit',
                                                lineHeight: 1.6,
                                                boxSizing: 'border-box'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                        />
                                        
                                        {/* Example instructions */}
                                        <div style={{ marginTop: '12px' }}>
                                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>Click to add examples:</div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                {[
                                                    { label: '🏆 Highlight expertise', text: 'Emphasize our team\'s extensive experience and past success in similar projects.' },
                                                    { label: '📜 Certifications', text: 'Prominently feature our ISO 27001 and SOC 2 certifications throughout the response.' },
                                                    { label: '🤝 Customer focus', text: 'Use a customer-centric tone emphasizing partnership and long-term support commitment.' },
                                                    { label: '💡 Innovation', text: 'Highlight innovative approaches and cutting-edge technologies we bring to the solution.' },
                                                    { label: '⚡ Response time', text: 'Emphasize our 24/7 support and rapid response SLAs.' },
                                                    { label: '📊 Evidence-based', text: 'Include specific metrics, case study data, and quantifiable outcomes where possible.' }
                                                ].map((example, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={() => setAiInstructions(prev => prev ? `${prev}\n${example.text}` : example.text)}
                                                        style={{
                                                            padding: '6px 12px',
                                                            fontSize: '12px',
                                                            background: 'white',
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '16px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.15s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.borderColor = '#667eea';
                                                            e.currentTarget.style.background = 'rgba(102,126,234,0.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.borderColor = '#e5e7eb';
                                                            e.currentTarget.style.background = 'white';
                                                        }}
                                                    >
                                                        {example.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Data Sources Configuration */}
                                    <div style={{
                                        marginBottom: '24px',
                                        padding: '16px',
                                        background: '#f9fafb',
                                        borderRadius: '10px',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                                            📚 Data Sources
                                        </div>
                                        
                                        {/* References only toggle */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={useReferencesOnly}
                                                onChange={(e) => setUseReferencesOnly(e.target.checked)}
                                                style={{ width: '16px', height: '16px' }}
                                            />
                                            <span style={{ fontSize: '13px', color: '#374151' }}>
                                                Use only uploaded documents (ignore AI general knowledge)
                                            </span>
                                        </label>
                                        
                                        {/* Web search toggle */}
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={enableWebSearch}
                                                onChange={(e) => setEnableWebSearch(e.target.checked)}
                                                style={{ width: '16px', height: '16px' }}
                                            />
                                            <span style={{ fontSize: '13px', color: '#374151' }}>
                                                Enable web search for additional context
                                            </span>
                                        </label>
                                    </div>
                                    
                                    {/* Web Sources (only shown if web search enabled) */}
                                    {enableWebSearch && (
                                        <div style={{
                                            marginBottom: '24px',
                                            padding: '16px',
                                            background: '#f0f9ff',
                                            borderRadius: '10px',
                                            border: '1px solid #bae6fd'
                                        }}>
                                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#0369a1', marginBottom: '12px' }}>
                                                🌐 Specific Web Sources (Optional)
                                            </div>
                                            <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#0284c7' }}>
                                                Add specific URLs or domains the AI should reference for up-to-date information.
                                            </p>
                                            
                                            {/* Existing web sources list */}
                                            {webSources.length > 0 && (
                                                <div style={{ marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    {webSources.map((source, idx) => (
                                                        <div key={idx} style={{
                                                            padding: '10px 12px',
                                                            background: 'white',
                                                            border: '1px solid #bae6fd',
                                                            borderRadius: '6px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '10px'
                                                        }}>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontSize: '13px', fontWeight: 500, color: '#0369a1', wordBreak: 'break-all' }}>
                                                                    {source.url}
                                                                </div>
                                                                {source.description && (
                                                                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                                                        {source.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <button
                                                                onClick={() => removeWebSource(idx)}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    padding: '4px',
                                                                    color: '#dc2626'
                                                                }}
                                                            >
                                                                <DeleteRegular style={{ fontSize: '14px' }} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {/* Add new web source */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                <input
                                                    type="text"
                                                    value={newWebSourceUrl}
                                                    onChange={(e) => setNewWebSourceUrl(e.target.value)}
                                                    placeholder="https://docs.microsoft.com/azure or docs.aws.amazon.com"
                                                    style={{
                                                        padding: '10px 12px',
                                                        fontSize: '13px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '6px',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                                <input
                                                    type="text"
                                                    value={newWebSourceDescription}
                                                    onChange={(e) => setNewWebSourceDescription(e.target.value)}
                                                    placeholder="Description (optional): e.g., Azure documentation"
                                                    style={{
                                                        padding: '10px 12px',
                                                        fontSize: '13px',
                                                        border: '1px solid #d1d5db',
                                                        borderRadius: '6px',
                                                        boxSizing: 'border-box'
                                                    }}
                                                />
                                                <button
                                                    onClick={addWebSource}
                                                    disabled={!newWebSourceUrl.trim()}
                                                    style={{
                                                        padding: '8px 14px',
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        background: newWebSourceUrl.trim() ? '#0369a1' : '#e5e7eb',
                                                        color: newWebSourceUrl.trim() ? 'white' : '#9ca3af',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: newWebSourceUrl.trim() ? 'pointer' : 'not-allowed',
                                                        alignSelf: 'flex-start'
                                                    }}
                                                >
                                                    + Add Source
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    
                                    <p style={{ 
                                        margin: '0', 
                                        padding: '12px 16px',
                                        background: '#eff6ff',
                                        border: '1px solid #93c5fd',
                                        borderRadius: '8px',
                                        fontSize: '13px', 
                                        color: '#1e40af' 
                                    }}>
                                        💡 These instructions and sources will guide the AI when generating content for each tender section.
                                    </p>
                                </>
                            ) : (
                                // Standard mode: Generation prompt UI
                                <>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{
                                    display: 'block',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#374151',
                                    marginBottom: '8px'
                                }}>
                                    Generation Prompt <span style={{ color: '#dc2626' }}>*</span>
                                </label>
                                <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#6b7280' }}>
                                    Describe what document you want to create. Be specific about the topic, 
                                    audience, and any special requirements.
                                </p>
                                <textarea
                                    value={generationPrompt}
                                    onChange={(e) => setGenerationPrompt(e.target.value)}
                                    placeholder="Based on the reference document, create a comprehensive design document for AWS landing zone architecture. Include sections for network topology, security controls, identity management, and logging/monitoring strategy..."
                                    rows={6}
                                    style={{
                                        width: '100%',
                                        padding: '16px',
                                        fontSize: '14px',
                                        border: '2px solid #e5e7eb',
                                        borderRadius: '10px',
                                        outline: 'none',
                                        resize: 'vertical',
                                        fontFamily: 'inherit',
                                        lineHeight: 1.6,
                                        boxSizing: 'border-box'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                />
                            </div>

                            {/* Quick start prompts */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    color: '#6b7280',
                                    marginBottom: '10px'
                                }}>
                                    Quick start templates:
                                </label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {QUICK_START_PROMPTS.map((item, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setGenerationPrompt(item.prompt)}
                                            style={{
                                                padding: '10px 14px',
                                                fontSize: '13px',
                                                border: generationPrompt === item.prompt ? '2px solid #667eea' : '1px solid #e5e7eb',
                                                borderRadius: '8px',
                                                background: generationPrompt === item.prompt ? 'rgba(102,126,234,0.05)' : '#f9fafb',
                                                color: '#374151',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                transition: 'all 0.15s'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (generationPrompt !== item.prompt) {
                                                    e.currentTarget.style.borderColor = '#667eea';
                                                    e.currentTarget.style.background = 'rgba(102,126,234,0.03)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (generationPrompt !== item.prompt) {
                                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                                    e.currentTarget.style.background = '#f9fafb';
                                                }
                                            }}
                                        >
                                            <span style={{ fontWeight: 500 }}>{item.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Data Source Options */}
                            <div style={{
                                marginTop: '20px',
                                padding: '16px',
                                background: '#f9fafb',
                                borderRadius: '10px',
                                border: '1px solid #e5e7eb'
                            }}>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px' }}>
                                    Generation Mode
                                </div>
                                
                                {/* Generation mode toggle */}
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                                    <button
                                        onClick={() => setGenerationMode('ai')}
                                        style={{
                                            flex: 1,
                                            padding: '12px',
                                            border: generationMode === 'ai' ? '2px solid #667eea' : '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            background: generationMode === 'ai' ? 'rgba(102,126,234,0.05)' : 'white',
                                            cursor: 'pointer',
                                            textAlign: 'left'
                                        }}
                                    >
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: generationMode === 'ai' ? '#667eea' : '#374151' }}>
                                            ⚡ AI Mode (Fast)
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                            Single-shot generation. Good for quick drafts.
                                        </div>
                                    </button>
                                    <button
                                        onClick={() => setGenerationMode('document_intelligence')}
                                        disabled={!startingDoc}
                                        style={{
                                            flex: 1,
                                            padding: '12px',
                                            border: generationMode === 'document_intelligence' ? '2px solid #667eea' : '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            background: generationMode === 'document_intelligence' ? 'rgba(102,126,234,0.05)' : 'white',
                                            cursor: startingDoc ? 'pointer' : 'not-allowed',
                                            textAlign: 'left',
                                            opacity: startingDoc ? 1 : 0.5
                                        }}
                                    >
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: generationMode === 'document_intelligence' ? '#667eea' : '#374151' }}>
                                            📄 Template Mode (Detailed)
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                            {startingDoc 
                                                ? 'Extracts template structure, generates each section with full detail.'
                                                : 'Requires a starting document (template)'}
                                        </div>
                                    </button>
                                </div>
                                
                                {generationMode === 'document_intelligence' && (
                                    <div style={{
                                        padding: '10px 12px',
                                        background: '#eff6ff',
                                        border: '1px solid #93c5fd',
                                        borderRadius: '6px',
                                        marginBottom: '16px',
                                        fontSize: '12px',
                                        color: '#1e40af'
                                    }}>
                                        💡 <strong>Template Mode:</strong> Uses Document Intelligence to extract your template structure, 
                                        then generates detailed content for each section using relevant data from reference documents. 
                                        Takes longer but produces more comprehensive output.
                                    </div>
                                )}
                                
                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px', marginTop: '16px' }}>
                                    Data Sources
                                </div>
                                
                                {/* References only toggle */}
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={useReferencesOnly}
                                        onChange={(e) => setUseReferencesOnly(e.target.checked)}
                                        style={{ width: '16px', height: '16px' }}
                                    />
                                    <span style={{ fontSize: '13px', color: '#374151' }}>
                                        Use only uploaded documents (ignore AI general knowledge)
                                    </span>
                                </label>
                                
                                {/* Web search toggle */}
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={enableWebSearch}
                                        onChange={(e) => setEnableWebSearch(e.target.checked)}
                                        style={{ width: '16px', height: '16px' }}
                                    />
                                    <span style={{ fontSize: '13px', color: '#374151' }}>
                                        Enable web search for additional context
                                    </span>
                                </label>
                                
                                {/* Allowed domains (only shown if web search enabled) */}
                                {enableWebSearch && (
                                    <div style={{ marginTop: '12px', marginLeft: '26px' }}>
                                        <input
                                            type="text"
                                            value={allowedDomains}
                                            onChange={(e) => setAllowedDomains(e.target.value)}
                                            placeholder="Restrict to sites: docs.microsoft.com, aws.amazon.com (optional)"
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                fontSize: '13px',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                                            Comma-separated list of domains, or leave empty for unrestricted search
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Summary */}
                            <div style={{
                                marginTop: '24px',
                                padding: '16px',
                                background: 'linear-gradient(135deg, rgba(102,126,234,0.05) 0%, rgba(118,75,162,0.05) 100%)',
                                borderRadius: '10px',
                                border: '1px solid rgba(102,126,234,0.2)'
                            }}>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                    📝 Document Summary
                                </div>
                                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                    <div><strong>Title:</strong> {title || '(not set)'}</div>
                                    <div><strong>Format:</strong> {format.toUpperCase()}</div>
                                    <div><strong>Starting doc:</strong> {startingDoc?.name || 'None'}</div>
                                    <div><strong>Reference docs:</strong> {referenceDocs.length > 0 ? `${referenceDocs.length} file${referenceDocs.length !== 1 ? 's' : ''}` : 'None'}</div>
                                </div>
                            </div>
                                </>
                            )}
                        </div>
                    )}
                    
                    {/* Step 4: Review & Start (tender mode only) */}
                    {step === 4 && workspaceMode === 'tender' && (
                        <div style={{ maxWidth: '520px', margin: '0 auto' }}>
                            {/* Full Summary */}
                            <div style={{
                                padding: '20px',
                                background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
                                borderRadius: '12px',
                                border: '1px solid rgba(102,126,234,0.25)',
                                marginBottom: '20px'
                            }}>
                                <div style={{ fontSize: '16px', fontWeight: 600, color: '#374151', marginBottom: '16px' }}>
                                    📋 Tender Response Configuration
                                </div>
                                
                                {/* Basic Info */}
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase' }}>
                                        Basic Information
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#374151' }}>
                                        <div><strong>Title:</strong> {title || '(not set)'}</div>
                                        <div><strong>Template:</strong> {startingDoc?.name || 'Not uploaded'}</div>
                                    </div>
                                </div>
                                
                                {/* Documents Summary */}
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase' }}>
                                        Documents by Role
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#374151' }}>
                                        <div>📋 Requirements: {referenceDocs.filter(d => docRoles[d.id] === 'requirements' || !docRoles[d.id]).length}</div>
                                        <div>📄 Evidence: {referenceDocs.filter(d => docRoles[d.id] === 'evidence').length}</div>
                                        <div>📏 Standards: {referenceDocs.filter(d => docRoles[d.id] === 'standards').length}</div>
                                        <div>💰 Commercial: {referenceDocs.filter(d => docRoles[d.id] === 'commercial').length}</div>
                                        {referenceDocs.filter(d => docRoles[d.id] === 'custom').length > 0 && (
                                            <div>✏️ Custom: {referenceDocs.filter(d => docRoles[d.id] === 'custom').length}</div>
                                        )}
                                    </div>
                                </div>
                                
                                {/* AI Instructions */}
                                {aiInstructions.trim() && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase' }}>
                                            AI Instructions
                                        </div>
                                        <div style={{ 
                                            fontSize: '13px', 
                                            color: '#374151',
                                            padding: '10px',
                                            background: 'rgba(255,255,255,0.6)',
                                            borderRadius: '6px',
                                            fontStyle: 'italic'
                                        }}>
                                            "{aiInstructions.substring(0, 150)}{aiInstructions.length > 150 ? '...' : ''}"
                                        </div>
                                    </div>
                                )}
                                
                                {/* Data Sources */}
                                <div>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase' }}>
                                        Data Sources
                                    </div>
                                    <div style={{ fontSize: '14px', color: '#374151' }}>
                                        <div>🔒 Documents only: {useReferencesOnly ? 'Yes' : 'No (allow general knowledge)'}</div>
                                        <div>🌐 Web search: {enableWebSearch ? 'Enabled' : 'Disabled'}</div>
                                        {enableWebSearch && webSources.length > 0 && (
                                            <div style={{ marginTop: '4px' }}>
                                                📎 Specific sources: {webSources.length} configured
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* What happens next */}
                            <div style={{
                                padding: '16px',
                                background: '#f0fdf4',
                                borderRadius: '10px',
                                border: '1px solid #86efac',
                                marginBottom: '16px'
                            }}>
                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#166534', marginBottom: '10px' }}>
                                    🚀 What happens next
                                </div>
                                <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#15803d', lineHeight: 1.8 }}>
                                    <li>Template document will be analyzed to extract sections</li>
                                    <li>Requirements from RFP/ITT documents will be extracted</li>
                                    <li>AI will prepare draft responses with evidence citations</li>
                                    <li>You'll review and refine each section in the TenderBuilder</li>
                                </ol>
                            </div>
                            
                            <p style={{ 
                                margin: '0', 
                                padding: '12px 16px',
                                background: '#eff6ff',
                                border: '1px solid #93c5fd',
                                borderRadius: '8px',
                                fontSize: '13px', 
                                color: '#1e40af' 
                            }}>
                                ✨ Click "Create Tender Workspace" to start the analysis. This may take a few minutes depending on document size.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '20px 28px',
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: '#fafafa'
                }}>
                    <button
                        onClick={() => step > 1 ? setStep((step - 1) as WizardStep) : handleClose()}
                        disabled={isCreating}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 20px',
                            background: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#4b5563',
                            cursor: isCreating ? 'not-allowed' : 'pointer'
                        }}
                    >
                        <ArrowLeftFilled style={{ fontSize: '14px' }} />
                        {step === 1 ? 'Cancel' : 'Back'}
                    </button>

                    <button
                        onClick={() => {
                            if (step < maxSteps) {
                                setStep((step + 1) as WizardStep);
                            } else {
                                handleCreate();
                            }
                        }}
                        disabled={!canProceed() || isCreating}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 24px',
                            background: canProceed() && !isCreating
                                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                : '#e5e7eb',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: canProceed() && !isCreating ? 'white' : '#9ca3af',
                            cursor: canProceed() && !isCreating ? 'pointer' : 'not-allowed',
                            minWidth: '140px',
                            justifyContent: 'center'
                        }}
                    >
                        {isCreating ? (
                            <>
                                <Spinner size={SpinnerSize.xSmall} />
                                <span>Creating...</span>
                            </>
                        ) : step < maxSteps ? (
                            <>
                                <span>Continue</span>
                                <ArrowRightFilled style={{ fontSize: '14px' }} />
                            </>
                        ) : workspaceMode === 'tender' ? (
                            <>
                                <SparkleRegular style={{ fontSize: '16px' }} />
                                <span>Create Tender Workspace</span>
                            </>
                        ) : (
                            <>
                                <SparkleRegular style={{ fontSize: '16px' }} />
                                <span>Create & Generate</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default CreateDocumentWorkspaceWizard;
